import {
  Flex,
  HitCounter,
  Loading,
  Table,
  TablePageSizeSelector,
  TableRowMenuCell,
  Text,
} from '@/components';
import {
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  datetimeFormat,
} from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowTemplatesWithPagination } from '../../hooks/template';
import { useWorkFlowTemplatePageSearchQueryStates } from '../../utils/search-query-states';
import { WorkflowTemplateDeleteModal } from './WorkflowTemplateDeleteModal';
import { useWorkflowTemplateDrawerState } from './WorkflowTemplateDrawerStateProvider';

export const WorkflowTemplateListTable: FC = () => {
  const { t } = useTranslation();
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const { setDrawerState } = useWorkflowTemplateDrawerState();
  const { searchQueryStates, setSearchQueryStates } =
    useWorkFlowTemplatePageSearchQueryStates();

  const templates = useWorkflowTemplatesWithPagination({
    page: searchQueryStates.page,
    pageSize: searchQueryStates.pageSize,
    cascader: searchQueryStates.cascader,
    q: searchQueryStates.q,
  });

  if (templates.isPending) {
    return <Loading />;
  }

  return (
    <>
      <Flex vertical gap={16}>
        <Flex justify="space-between" gap={8} align="center">
          <HitCounter
            count={templates.data?.count ?? 0}
            label={t('relevant')}
          />
          <Flex gap={8} align="center">
            <TablePageSizeSelector
              defaultPageSize={searchQueryStates.pageSize}
              onChange={(newPageSize) => {
                setSearchQueryStates((prev) => {
                  return {
                    ...prev,
                    page: 1,
                    pageSize: newPageSize,
                  };
                });
              }}
            />
          </Flex>
        </Flex>
        <Table
          dataSource={templates.data?.results}
          rowKey="id"
          columns={[
            {
              title: t('workflow.template-name'),
              key: 'name',
              dataIndex: 'name',
            },
            {
              title: t('workflow.organization'),
              key: 'organizations',
              width: 300,
              render: (_, { organizations }) => (
                <Text
                  weight="regular"
                  variant="body"
                  maxRows={3}
                  ellipsis={{
                    tooltip: {
                      styles: {
                        root: {
                          maxHeight: 400,
                          overflow: 'scroll',
                          whiteSpace: 'pre-line',
                        },
                      },
                    },
                  }}
                  text={
                    organizations.length
                      ? organizations
                          .map((organization) => organization.organizationName)
                          .join('\n')
                      : null
                  }
                />
              ),
            },
            {
              title: t('created-datetime'),
              key: 'createdAt',
              dataIndex: 'createdAt',
              width: TABLE_DATETIME_WIDTH,
              render: (value) => datetimeFormat({ datetime: value }),
            },
            {
              title: t('updated-datetime'),
              key: 'updatedAt',
              dataIndex: 'updatedAt',
              width: TABLE_DATETIME_WIDTH,
              render: (value) => datetimeFormat({ datetime: value }),
            },
            {
              key: 'actions',
              fixed: 'right' as const,
              width: TABLE_ROW_MENU_WIDTH,
              onCell: () => ({
                style: { minWidth: TABLE_ROW_MENU_WIDTH },
              }),
              render: (_, record) => (
                <TableRowMenuCell
                  onEdit={() =>
                    setDrawerState({
                      open: true,
                      operation: 'update',
                      templateId: record.id,
                      organizationIds: record.organizationIds,
                    })
                  }
                  items={[
                    {
                      key: 'delete',
                      label: t('delete'),
                      danger: true,
                      onClick: () => setDeleteTemplateId(record.id),
                    },
                  ]}
                />
              ),
            },
          ]}
          pagination={{
            current: searchQueryStates.page,
            total: templates.data?.count,
            showSizeChanger: false,
            position: ['bottomRight'],
            pageSize: searchQueryStates.pageSize,
          }}
          onChange={(pagination, _, __, extra) => {
            switch (extra.action) {
              case 'paginate':
                if (pagination.current) {
                  setSearchQueryStates((prev) => ({
                    ...prev,
                    page: pagination.current,
                  }));
                }
                return;
              default: {
                return;
              }
            }
          }}
        />
      </Flex>
      <WorkflowTemplateDeleteModal
        deleteTemplateId={deleteTemplateId}
        onClose={() => setDeleteTemplateId(null)}
      />
    </>
  );
};
